import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  addDoc,
  writeBatch,
  doc,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import {
  getFunctions,
  httpsCallable,
  FunctionsErrorCode,
} from 'firebase/functions';
import { ChevronLeft } from 'lucide-react';
import {
  addWeeks,
  addMonths,
  parse,
  isBefore,
  parseISO,
  setHours,
  setMinutes,
  endOfDay,
} from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../components/ui'; // Assuming Button accepts translated text
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { db, app } from '../lib/firebase';
import { usePatientData } from '../hooks/usePatientData';
import { usePatientSessions } from '../hooks/usePatientSessions';
import { PatientDetailForm } from '../components/patient/PatientDetailForm'; // Assume localized
import { PatientSessionList } from '../components/patient/PatientSessionList'; // Assume localized
import { PatientReviewEmailStatus } from '../components/patient/PatientReviewEmailStatus';
import { SessionPriceTier, Session, Patient, BillingProfile } from '../types/firestoreTypes';
import {
  SESSION_STATUS,
  CREATION_METHOD,
  SessionType,
} from '../types/constants';

// Interface for session form data remains the same
interface SessionFormData {
  date: string;
  time: string;
  priceTierId: string;
  notes: string;
  isRecurring: boolean;
  frequency?: 'Weekly' | 'Every 2 Weeks' | 'Monthly';
  endDate?: string;
  sessionType: SessionType;
}

// Interfaces for Cloud Function request/response remain the same
interface GenerateInvoiceResponse {
  success: boolean;
  invoiceId?: number;
  invoiceNumber?: string;
  error?: string;
  sendStatus?: string;
}

interface GenerateInvoiceRequest {
  patientId: string;
  sessionIds: string[];
}

const PatientDetailPage: React.FC = () => {
  const { t } = useTranslation(); // Get the t function
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { user, therapist } = useAuth(); // Get therapist data
  const { addToast } = useToast();
  const functions = getFunctions(app, 'europe-west1');

  // State variables remain mostly the same
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(
    new Set()
  );
  const [isGeneratingBulkInvoice, setIsGeneratingBulkInvoice] = useState(false);
  const [priceTiers, setPriceTiers] = useState<SessionPriceTier[]>([]);
  const [isLoadingPriceTiers, setIsLoadingPriceTiers] = useState(true);
  const [selectedDateFilter, setSelectedDateFilter] = useState('all'); // Values used by hook - show all sessions
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all'); // Values used by hook
  const [sortColumn, setSortColumn] = useState('sessionDateTime'); // Values used by hook
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc'); // Values used by hook - newest first
  const [isSavingPatient, setIsSavingPatient] = useState(false);

  // Add state for billing profiles
  const [therapistBillingProfiles, setTherapistBillingProfiles] = useState<BillingProfile[]>([]);

  // Use custom hooks for data fetching
  const {
    patient,
    isLoading: isPatientLoading,
    error: patientError,
    refresh: refreshPatientData,
  } = usePatientData(patientId || '');

  const {
    sessions,
    isLoading: isSessionsLoading,
    error: sessionsError, // Hook might return raw error or message
    hasMore,
    loadMore,
    isFetchingMore,
    refresh: refreshSessions,
  } = usePatientSessions(
    patientId || '',
    selectedDateFilter,
    selectedStatusFilter,
    sortColumn,
    sortDirection
  );

  // Fetch price tiers
  useEffect(() => {
    const fetchPriceTiers = async () => {
      if (!user) {
        setIsLoadingPriceTiers(false);
        return;
      }
      setIsLoadingPriceTiers(true);
      try {
        const priceTiersRef = collection(db, 'sessionPriceTiers');
        const q = query(
          priceTiersRef,
          where('therapistId', '==', user.uid),
          where('isArchived', '==', false),
          orderBy('tierName')
        );
        const priceTiersSnapshot = await getDocs(q);
        const tiers = priceTiersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<SessionPriceTier, 'id'>),
        }));
        setPriceTiers(tiers);
      } catch (err) {
        console.error('Error fetching price tiers:', err);
        // Use translated toast message
        addToast({
          type: 'error',
          message: t('patients.errors.loadPriceTiersFailed'),
        });
      } finally {
        setIsLoadingPriceTiers(false);
      }
    };
    fetchPriceTiers();
  }, [user, addToast, t]); // Add t dependency

  // Add effect to process billing profiles
  useEffect(() => {
    if (therapist?.billingProfiles) {
      const profilesArray = Object.entries(therapist.billingProfiles).map(
        ([id, profile]) => ({
          ...profile,
          profileId: id,
        })
      );
      setTherapistBillingProfiles(profilesArray);
    } else {
      setTherapistBillingProfiles([]);
    }
  }, [therapist]);

  // Handle sorting changes (logic remains the same)
  const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  // Handle session selection changes (logic remains the same)
  const handleSelectionChange = (newSelectedIds: Set<string>) => {
    setSelectedSessionIds(newSelectedIds);
  };

  // Generate bulk invoice handler
  const handleGenerateBulkInvoice = useCallback(async () => {
    const idsToInvoice = Array.from(selectedSessionIds);

    if (idsToInvoice.length === 0) {
      addToast({
        type: 'warning',
        message: t('patientDetail.warnings.selectSessions'),
      });
      return;
    }

    const selectedSessions = sessions.filter((session) =>
      selectedSessionIds.has(session.id)
    );

    if (
      !selectedSessions.every(
        (session) => session.status === SESSION_STATUS.COMPLETED
      )
    ) {
      addToast({
        type: 'warning',
        message: t('patientDetail.warnings.invoiceOnlyCompleted'),
      });
      return;
    }

    if (selectedSessions.some((session) => session.invoiceId)) {
      addToast({
        type: 'warning',
        message: t('patientDetail.warnings.alreadyInvoiced'),
      });
      return;
    }

    const currencies = new Set(
      selectedSessions.map((session) => session.price.currency)
    );
    if (currencies.size > 1) {
      addToast({
        type: 'warning',
        message: t('patientDetail.warnings.currencyMismatch'),
      });
      return;
    }

    setIsGeneratingBulkInvoice(true);
    try {
      const callGenerateBulk = httpsCallable<
        GenerateInvoiceRequest,
        GenerateInvoiceResponse
      >(functions, 'generateBulkBillingoInvoice');
      const result = await callGenerateBulk({
        patientId: patientId!,
        sessionIds: idsToInvoice,
      });

      if (result.data.success) {
        addToast({
          type: 'success',
          message: t('patientDetail.success.bulkInvoiceGenerated', {
            invoiceNumber: result.data.invoiceNumber
              ? `#${result.data.invoiceNumber}`
              : '',
            sendStatus: result.data.sendStatus || 'N/A',
          }),
          duration: 7000,
        });
        setSelectedSessionIds(new Set());
        refreshSessions();
      } else {
        addToast({
          type: 'error',
          message:
            result.data.error || t('patientDetail.errors.bulkInvoiceFailed'),
        });
      }
    } catch (err: unknown) {
      console.error('Error generating bulk invoice:', err);
      let messageKey = 'patientDetail.errors.bulkInvoiceUnexpected';
      let details = '';
      if (
        typeof err === 'object' &&
        err !== null &&
        'code' in err &&
        'message' in err
      ) {
        const httpsError = err as {
          code: string;
          message: string;
          details?: unknown;
        };
        messageKey = 'patientDetail.errors.bulkInvoiceDetail';
        details = `${httpsError.message} (${httpsError.code})`;
      }
      addToast({ type: 'error', message: t(messageKey, { details }) });
    } finally {
      setIsGeneratingBulkInvoice(false);
    }
  }, [
    selectedSessionIds,
    sessions,
    patientId,
    addToast,
    functions,
    refreshSessions,
    t,
  ]);

  // Save session handler
  const handleSessionSave = useCallback(
    async (sessionData: SessionFormData) => {
      if (!user || !patient || !patientId) {
        addToast({
          type: 'error',
          message: t('patientDetail.errors.saveSessionMissingData'),
        });
        throw new Error('User or patient data missing.');
      }
      const selectedPriceTier = priceTiers.find(
        (tier) => tier.id === sessionData.priceTierId
      );
      if (!selectedPriceTier) {
        addToast({
          type: 'error',
          message: t('patientDetail.errors.saveSessionInvalidTier'),
        });
        throw new Error('Selected price tier not found.');
      }

      try {
        const [hours, minutes] = sessionData.time.split(':').map(Number);
        const baseDate = parse(sessionData.date, 'yyyy-MM-dd', new Date());
        const sessionDateTime = setMinutes(setHours(baseDate, hours), minutes);

        if (isNaN(sessionDateTime.getTime())) {
          throw new Error(
            `Invalid date/time constructed from ${sessionData.date} ${sessionData.time}`
          );
        }

        if (sessionData.isRecurring) {
          // Recurring session logic
          if (!sessionData.endDate || !sessionData.frequency) {
            throw new Error(t('patientDetail.errors.recurringFieldsRequired'));
          }
          const endDate = endOfDay(parseISO(sessionData.endDate));
          if (!isBefore(sessionDateTime, endDate)) {
            throw new Error(t('patientDetail.errors.endDateBeforeStartDate'));
          }

          const recurringSeriesId = uuidv4();
          const batch = writeBatch(db);
          let currentDate = sessionDateTime;
          let sessionCount = 0;
          const MAX_SESSIONS = 104; // Safety limit

          while (
            isBefore(currentDate, endDate) &&
            sessionCount < MAX_SESSIONS
          ) {
            const sessionRef = doc(collection(db, 'sessions'));
            // Session data structure remains the same
            batch.set(sessionRef, {
              therapistId: user.uid,
              patientId: patientId,
              patientName: patient.name,
              sessionDateTime: Timestamp.fromDate(currentDate),
              priceTierId: selectedPriceTier.id,
              price: {
                amount: selectedPriceTier.price,
                currency: selectedPriceTier.currency,
              },
              durationMinutes: selectedPriceTier.durationMinutes,
              notes: sessionData.notes,
              sessionType: sessionData.sessionType,
              status: SESSION_STATUS.SCHEDULED,
              creationMethod: CREATION_METHOD.RECURRING,
              recurringSeriesId,
              createdAt: Timestamp.now(),
            });
            sessionCount++;

            switch (sessionData.frequency) {
              case 'Weekly':
                currentDate = addWeeks(currentDate, 1);
                break;
              case 'Every 2 Weeks':
                currentDate = addWeeks(currentDate, 2);
                break;
              case 'Monthly':
                currentDate = addMonths(currentDate, 1);
                break;
              default:
                throw new Error(t('patientDetail.errors.invalidFrequency'));
            }
          }

          if (sessionCount === 0)
            throw new Error(t('patientDetail.errors.noSessionsGenerated'));

          await batch.commit();
          // Use translated success message with count
          addToast({
            type: 'success',
            message: t('patientDetail.success.recurringScheduled', {
              count: sessionCount,
            }),
          });
          refreshSessions();
        } else {
          // Single session logic
          const newSessionRef = await addDoc(collection(db, 'sessions'), {
            therapistId: user.uid,
            patientId: patientId,
            patientName: patient.name,
            sessionDateTime: Timestamp.fromDate(sessionDateTime),
            priceTierId: selectedPriceTier.id,
            price: {
              amount: selectedPriceTier.price,
              currency: selectedPriceTier.currency,
            },
            durationMinutes: selectedPriceTier.durationMinutes,
            notes: sessionData.notes,
            sessionType: sessionData.sessionType,
            status: SESSION_STATUS.SCHEDULED,
            creationMethod: CREATION_METHOD.MANUAL,
            createdAt: Timestamp.now(),
          });

          const newSession: Session = {
            id: newSessionRef.id,
            therapistId: user.uid,
            patientId: patientId,
            patientName: patient.name,
            sessionDateTime: Timestamp.fromDate(sessionDateTime),
            priceTierId: selectedPriceTier.id,
            price: {
              amount: selectedPriceTier.price,
              currency: selectedPriceTier.currency,
            },
            durationMinutes: selectedPriceTier.durationMinutes,
            notes: sessionData.notes,
            sessionType: sessionData.sessionType,
            status: SESSION_STATUS.SCHEDULED,
            creationMethod: CREATION_METHOD.MANUAL,
            createdAt: Timestamp.now(),
          };

          addToast({
            type: 'success',
            message: t('patientDetail.success.sessionScheduled'),
          });
          refreshSessions();
        }
      } catch (err) {
        console.error('Error saving session(s):', err);
        const message =
          err instanceof Error
            ? err.message
            : t('patientDetail.errors.scheduleFailedGeneric');
        // Use translated error message with details
        addToast({
          type: 'error',
          message: t('patientDetail.errors.scheduleFailed', {
            message: message,
          }),
        });
        throw err; // Re-throw for promise rejection
      }
    },
    [user, patient, patientId, priceTiers, addToast, refreshSessions, t]
  );

  // --- Render Logic ---

  // Loading state
  if (isPatientLoading || isLoadingPriceTiers) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#7F56D9] border-t-transparent"></div>
        <span className="ml-3 text-gray-600">
          {t('patientDetail.status.loading')}
        </span>{' '}
        {/* Translate */}
      </div>
    );
  }

  // Patient loading error state
  if (patientError) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-700 border border-red-200">
        {/* Display generic translated error, patientError might be an object */}
        {t('patientDetail.errors.loadPatientFailed')}
        {typeof patientError === 'string' && `: ${patientError}`}
      </div>
    );
  }

  // Patient not found state
  if (!patient) {
    return (
      <div className="p-4 bg-yellow-50 rounded-lg text-yellow-700 border border-yellow-200 text-center">
        <p>{t('patientDetail.notFound.message')}</p>
        <Button
          variant="secondary"
          onClick={() => navigate('/patients')}
          className="mt-2"
        >
          {t('patientDetail.notFound.backButton')}
        </Button>
      </div>
    );
  }

  // Main component render
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ChevronLeft className="w-4 h-4" />}
          onClick={() => navigate(-1)}
          aria-label={t('patientDetail.buttons.back')}
        >
          {t('patientDetail.buttons.back')}
        </Button>
        <h1 className="text-2xl font-semibold text-[#101828] truncate">
          {patient.name}
        </h1>
      </div>

      {/* Patient Details Form */}
      <PatientDetailForm 
        patient={patient} 
        priceTiers={priceTiers} 
        billingProfiles={therapistBillingProfiles}
        onSave={async (pid, updateData) => {
          setIsSavingPatient(true);
          try {
            const patientRef = doc(db, 'patients', pid);
            await updateDoc(patientRef, updateData);
            addToast({
              type: 'success',
              message: t('patientDetailForm.toasts.updateSuccess', { 
                name: updateData.name || patient?.name 
              }),
            });
            if (refreshPatientData) {
              await refreshPatientData();
            }
          } catch (formSaveError) {
            console.error('Error updating patient details:', formSaveError);
            addToast({
              type: 'error',
              message: t('patientDetailForm.toasts.updateFailed'),
            });
          } finally {
            setIsSavingPatient(false);
          }
        }}
        isSaving={isSavingPatient}
      />

      {/* Email Status Section */}
      <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {t('patientDetail.emailStatus.heading')}
            </h3>
            <PatientReviewEmailStatus 
              patient={patient} 
              className="mb-2" 
            />
          </div>
        </div>
      </div>

      {/* Session List */}
      <PatientSessionList
        patientId={patient.id}
        patientName={patient.name}
        defaultPriceTierId={
          patient.defaultPriceTierId ||
          (priceTiers.length > 0 ? priceTiers[0].id : '')
        }
        priceTiers={priceTiers}
        sessions={sessions}
        isLoading={isSessionsLoading}
        error={
          sessionsError ? t('patientDetail.errors.loadSessionsFailed') : null
        }
        hasMore={hasMore}
        onLoadMore={loadMore}
        isFetchingMore={isFetchingMore}
        selectedSessionIds={selectedSessionIds}
        onSelectionChange={handleSelectionChange}
        onGenerateBulkInvoice={handleGenerateBulkInvoice}
        isGeneratingBulkInvoice={isGeneratingBulkInvoice}
        onSessionSave={handleSessionSave as any}
        onSortChange={handleSortChange}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        patient={patient}
        therapistBookingLink={therapist?.bookingLink}
        selectedDateFilter={selectedDateFilter}
        selectedStatusFilter={selectedStatusFilter}
        onDateFilterChange={setSelectedDateFilter}
        onStatusFilterChange={setSelectedStatusFilter}
      />
    </div>
  );
};

export default PatientDetailPage;
